import { create } from "zustand";
import { ensureProtocol, normalizeUrl } from "@/utils/apiUtils";

export interface AppConfig {
  enderecoSite: string;
  centroProducao?: string | null;
  centroProducaoAlt?: string | null;
}

const STORAGE_KEY = "app_config_v1";

function getDefaultBaseUrl(): string {
  const env = import.meta.env;
  const fromVite = env.VITE_API_BASE_URL as string | undefined;
  const fromDefault = (env as any).DEFAULT_BACKEND_URL as string | undefined;
  const base = fromVite || fromDefault || "";
  return base ? ensureProtocol(base) : "";
}

interface ConfigState {
  config: AppConfig;
  baseUrl: () => string;
  loadFromStorage: () => void;
  saveToStorage: () => void;
  setBaseUrl: (url: string) => void;
  setCentroProducao: (value: string | null) => void;
  setCentroProducaoAlt: (value: string | null) => void;
  reset: () => void;
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  config: {
    enderecoSite: getDefaultBaseUrl(),
    centroProducao: null,
    centroProducaoAlt: null,
  },

  baseUrl: () => (get().config.enderecoSite || "").replace(/\/+$/g, ""),

  loadFromStorage: () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as AppConfig;
      set({
        config: {
          enderecoSite: parsed.enderecoSite || getDefaultBaseUrl(),
          centroProducao: parsed.centroProducao ?? null,
          centroProducaoAlt: parsed.centroProducaoAlt ?? null,
        },
      });
    } catch (e) {
      console.warn("Falha ao carregar configuração. Restaurando defaults.");
      get().reset();
    }
  },

  saveToStorage: () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(get().config));
    } catch {
      console.warn("Falha ao salvar configuração no storage.");
    }
  },

  setBaseUrl: (url) => {
    const normalized = normalizeUrl(ensureProtocol(url), "");
    set((s) => ({
      config: { ...s.config, enderecoSite: normalized.replace(/\/+$/g, "") },
    }));
    get().saveToStorage();
  },

  setCentroProducao: (value) => {
    set((s) => ({ config: { ...s.config, centroProducao: value } }));
    get().saveToStorage();
  },

  setCentroProducaoAlt: (value) => {
    set((s) => ({ config: { ...s.config, centroProducaoAlt: value } }));
    get().saveToStorage();
  },

  reset: () => {
    set({
      config: {
        enderecoSite: getDefaultBaseUrl(),
        centroProducao: null,
        centroProducaoAlt: null,
      },
    });
    get().saveToStorage();
  },
}));
